import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fetchPlatformLayouts } from '../api/layout';

/**
 * LayoutConfigContext — provides platform header + footer config to
 * the citizen-side <Header> and <Footer> components.
 *
 * 0.36.293 — One fetch per app mount; both layouts come back in one
 * payload from /api/layout. If the fetch fails (network, backend
 * down, whatever), both consumers fall back to their hard-coded /
 * translated defaults so the page never breaks visually because the
 * layout config didn't load.
 *
 * `refresh()` re-fetches from the API — admin pages call this after a
 * successful save so any open citizen-side tab updates without a hard
 * reload. The provider also exposes `setLocal()` for optimistic
 * in-place updates from admin screens (the admin form's Save handler
 * sets it as soon as the PUT succeeds, before the next refresh).
 */
const LayoutConfigContext = createContext(null);

/* 0.46.78 - The last header and footer this browser saw, so the next
 * visit paints them at once. Before, the header showed the default
 * (Henaket blue) until /api/layout answered, then changed to the
 * admin's color: a visible flash on every page load. The fetch still
 * runs and replaces the cached copy. The cache is a convenience only:
 * private windows and blocked storage simply start without it. */
const CACHE_KEY = 'gt18.layout.v1';

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    const data = raw ? JSON.parse(raw) : null;
    return data && (data.header || data.footer) ? data : null;
  } catch {
    return null;
  }
}

function writeCache(header, footer) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ header, footer }));
  } catch {
    /* storage full or blocked: the next visit fetches as before */
  }
}


export function LayoutConfigProvider({ children }) {
  const [config, setConfig] = useState(() => {
    const cached = typeof window !== 'undefined' ? readCache() : null;
    return {
      header: cached ? cached.header || null : null,
      footer: cached ? cached.footer || null : null,
      // With a cached copy the page draws it straight away.
      loading: !cached,
      error: null,
    };
  });

  const refresh = useCallback(async () => {
    try {
      const data = await fetchPlatformLayouts();
      setConfig({
        header: data.header || null,
        footer: data.footer || null,
        loading: false,
        error: null,
      });
    } catch (e) {
      // Fall back gracefully. Page renders with default Henaket
      // styling; the admin can still navigate to the config screen
      // and fix things even if the public fetcher failed.
      setConfig(c => ({ ...c, loading: false, error: e.message || String(e) }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Keep the cache in step with what is shown (a fetch, or an admin's save).
  useEffect(() => {
    if (!config.loading && !config.error) writeCache(config.header, config.footer);
  }, [config.header, config.footer, config.loading, config.error]);

  // Optimistic local update — admin save handler calls this so the
  // page reflects the change immediately, without waiting for the
  // next refresh.
  const setLocal = useCallback((key, nextConfig) => {
    setConfig(c => ({ ...c, [key]: nextConfig }));
  }, []);

  return (
    <LayoutConfigContext.Provider value={{
      header: config.header,
      footer: config.footer,
      loading: config.loading,
      error: config.error,
      refresh,
      setLocal,
    }}>
      {children}
    </LayoutConfigContext.Provider>
  );
}


export function useLayoutConfig() {
  const ctx = useContext(LayoutConfigContext);
  if (!ctx) {
    // Defensive: if a component is rendered outside the provider,
    // return shape-compatible defaults so it doesn't crash. Should
    // never happen in practice — App.jsx wraps everything.
    return { header: null, footer: null, loading: false, error: null,
             refresh: () => {}, setLocal: () => {} };
  }
  return ctx;
}
